import { PHMS_HEARING_TERMINOLOGY } from "../../phms-hearing.terminology";
import { TerminologyIdentityDirective } from "@app/shared/terminology/terminology-identity.directive";
import { CommonModule } from "@angular/common";
import { AfterViewInit, Component, EventEmitter, Input, OnChanges, OnDestroy, Output, Signal, SimpleChanges, ViewChild } from "@angular/core";
import { Subscription } from "rxjs";
import { EzFormComponent } from "@template-ui/components/ez-form/ez-form.component";
import { InputChangeEvent, InputConfig } from "@ez-library/types/ez-form.types";
import { UiLibraryModule } from "@template-ui/template-ui.module";


/** Hearing-scoped working notes; structured analysis lives in the separate SDMF destination. */
@Component({
  selector: "phms-pre-hearing-analysis-notes-section",
  standalone: true,
  imports: [CommonModule, UiLibraryModule, TerminologyIdentityDirective],
  template: `
    <div class="working-notes" data-testid="pre-hearing-analysis-notes">
      <p class="notes-help"><span [appTerminologyIdentity]="terminologyUsage.notesPlaceholder">{{ notesHelp }}</span>
        Other authorized users can access these notes. Changes save automatically.</p>
      <ui-form [config]="notesConfig" [formValues]="editorValues" [disabled]="!canEdit()"></ui-form>
    </div>
  `,
  styles: `
    :host { display: block; min-width: 0; }
    .working-notes { min-width: 0; color: var(--custom-text-secondary-color); }
    .notes-help { margin: 0 0 1rem; font-size: .875rem; line-height: 1.5; color: var(--custom-text-muted-color); }
    ui-form { display: block; min-width: 0; }
    :host ::ng-deep .ck-editor__editable { min-height: 16rem; }
  `,
})
export class PreHearingAnalysisNotesSectionComponent implements AfterViewInit, OnChanges, OnDestroy {
  readonly terminologyUsage = PHMS_HEARING_TERMINOLOGY.preHearing;
  @Input() notesHelp = "";
  @ViewChild(EzFormComponent) private notesForm?: EzFormComponent;
  private notesChanges?: Subscription;
  @Input() notesConfig: InputConfig[] = [];
  @Input() notesValues: Record<string, unknown> = {};
  @Input({ required: true }) canEdit!: Signal<boolean>;
  editorValues: Record<string, unknown> = {};

  @Output() inputChange = new EventEmitter<InputChangeEvent>();
  @Output() notesValuesChange = new EventEmitter<Record<string, unknown>>();

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["notesValues"]) {
      // Hydrate on mount or external reload without feeding each editor change back into its controls.
      this.editorValues = { ...this.notesValues };
    }
  }

  ngAfterViewInit(): void {
    // Observe the editor model immediately so switching tasks before autosave runs keeps the draft.
    this.notesChanges = this.notesForm?.theFormGroup.valueChanges.subscribe(() => {
      this.onNotesValuesChange(this.notesForm?.theFormGroup.getRawValue());
    });
  }

  ngOnDestroy(): void {
    this.notesChanges?.unsubscribe();
  }

  onNotesValuesChange(values: unknown): void {
    // The editor's DOM input event precedes its form-control update. Use the settled model
    // so the saved HTML includes the final keystroke and formatting changes.
    if (!values || typeof values !== "object" || !("privateNotes" in values)) return;
    const privateNotes = typeof values.privateNotes === "string" ? values.privateNotes : "";
    if (!this.canEdit() || privateNotes === this.notesValues["privateNotes"]) return;
    // Keep the shared draft current without rebinding the editor on every keystroke.
    this.notesValues["privateNotes"] = privateNotes;
    this.notesValuesChange.emit(this.notesValues);
    this.inputChange.emit({ name: "privateNotes", value: privateNotes });
  }
}
